import React, { useEffect } from 'react';
import { Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useBlockUser } from '@/hooks/useBlockUser';

interface BlockUserButtonProps {
  userId: string;
  username?: string;
  variant?: 'button' | 'menuItem';
  onBlocked?: () => void;
}

export const BlockUserButton: React.FC<BlockUserButtonProps> = ({
  userId,
  username,
  variant = 'button',
  onBlocked
}) => {
  const { isBlocked, isLoading, checkBlockStatus, blockUser, unblockUser } = useBlockUser(userId);

  useEffect(() => {
    checkBlockStatus();
  }, [checkBlockStatus]);

  const handleAction = async () => {
    if (isBlocked) {
      await unblockUser();
    } else {
      const success = await blockUser();
      if (success && onBlocked) {
        onBlocked();
      }
    }
  };

  if (variant === 'menuItem') {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button
            className="flex items-center w-full px-2 py-1.5 text-sm text-red-400 hover:bg-red-500/10 rounded-sm cursor-pointer"
            disabled={isLoading}
          >
            <Ban className="h-4 w-4 mr-2" />
            {isBlocked ? 'Desbloquear usuário' : 'Bloquear usuário'}
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-gray-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {isBlocked ? 'Desbloquear usuário?' : 'Bloquear usuário?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {isBlocked 
                ? `${username || 'Este usuário'} poderá ver seu perfil e enviar mensagens novamente.`
                : `${username || 'Este usuário'} não poderá mais ver seu perfil, enviar mensagens ou interagir com você.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 border-white/20 text-white hover:bg-white/20">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              className={isBlocked 
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-red-600 hover:bg-red-700 text-white"
              }
            >
              {isBlocked ? 'Desbloquear' : 'Bloquear'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isLoading}
          className={isBlocked 
            ? "border-green-500/30 text-green-400 hover:bg-green-500/10"
            : "border-red-500/30 text-red-400 hover:bg-red-500/10"
          }
        >
          <Ban className="h-4 w-4 mr-2" />
          {isBlocked ? 'Desbloquear' : 'Bloquear'}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-gray-900 border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">
            {isBlocked ? 'Desbloquear usuário?' : 'Bloquear usuário?'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-white/60">
            {isBlocked 
              ? `${username || 'Este usuário'} poderá ver seu perfil e enviar mensagens novamente.`
              : `${username || 'Este usuário'} não poderá mais ver seu perfil, enviar mensagens ou interagir com você.`
            }
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/10 border-white/20 text-white hover:bg-white/20">
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleAction}
            className={isBlocked 
              ? "bg-green-600 hover:bg-green-700 text-white"
              : "bg-red-600 hover:bg-red-700 text-white"
            }
          >
            {isBlocked ? 'Desbloquear' : 'Bloquear'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
